import { useMemo } from 'react';
import { AnyVariables } from 'urql';

import { FilterOption } from '@/components/Filter/types';
import { UseFilterValueParams } from '@/components/FilterNew/types';
import usePagination from '@/hooks/usePagination';

type AdapterType = { selectedOptions: FilterOption[]; resolvedValue: string[] };

function useFilterValue<
  Query extends object,
  T extends string,
  Variables extends AnyVariables = AnyVariables,
>({
  value,
  useQuery,
  edgeKey,
  variables,
  getFilterOption,
}: UseFilterValueParams<Query, T, Variables>) {
  const { isFetching, data } = usePagination(useQuery, variables, {
    edgeKey,
    pause: !value.length,
    pageSize: value.length,
  });

  const { selectedOptions, resolvedValue } = useMemo(
    () =>
      value.length
        ? data.reduce<AdapterType>(
            (accumulator, dataValue) => {
              const filterOption = getFilterOption(dataValue);
              accumulator.selectedOptions.push(filterOption);
              accumulator.resolvedValue.push(filterOption.label);
              return accumulator;
            },
            { selectedOptions: [], resolvedValue: [] },
          )
        : { selectedOptions: [], resolvedValue: [] },
    [data, getFilterOption, value.length],
  );

  return {
    fetching: isFetching,
    resolvedValue,
    selectedOptions,
  };
}

export default useFilterValue;
